import { Chip, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { theme } from "../theme/theme";

/**
 * PFS (Probabilistic Flakiness Score) thresholds and color coding
 * Based on Facebook's research: https://engineering.fb.com/2020/12/10/developer-tools/probabilistic-flakiness/
 */
export const pfsThresholds = {
  reliable: {
    max: 0.05,
    color: theme.palette.success.main,
    label: "Reliable",
    description: "Very reliable test, flakiness is minimal",
  },
  moderate: {
    max: 0.15,
    color: theme.palette.warning.light,
    label: "Moderate",
    description: "Moderate flakiness, monitor but generally acceptable",
  },
  concerning: {
    max: 0.3,
    color: theme.palette.warning.main,
    label: "Concerning",
    description: "Concerning level of flakiness, should be investigated",
  },
  highly_flaky: {
    max: 1,
    color: theme.palette.error.main,
    label: "Highly Flaky",
    description: "Highly flaky, needs immediate attention or disabling",
  },
};

export type PfsCategory = keyof typeof pfsThresholds;

/**
 * Get the PFS category based on the score
 * @param score PFS score (0.0 - 1.0)
 * @returns PFS category
 */
export function getPfsCategory(score: number): PfsCategory {
  if (score < 0.05) return "reliable";
  if (score < 0.15) return "moderate";
  if (score < 0.3) return "concerning";
  return "highly_flaky";
}

/**
 * PFS Score Chip component with color coding
 * @param score PFS score (0.0 - 1.0)
 * @param showTooltip Whether to show tooltip with category description
 * @returns Chip component with color-coded PFS score
 */
export function PfsScoreChip({
  score,
  showTooltip = true,
}: {
  score: number;
  showTooltip?: boolean;
}) {
  const category = getPfsCategory(score);
  const config = pfsThresholds[category];

  const chip = (
    <Chip
      label={`${(score * 100).toFixed(1)}%`}
      size="small"
      sx={{
        backgroundColor: alpha(config.color, 0.15),
        color: config.color,
        fontWeight: 600,
      }}
    />
  );

  if (showTooltip) {
    return <Tooltip title={`${config.label}: ${config.description}`}>{chip}</Tooltip>;
  }

  return chip;
}
